import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  X,
  Printer,
  ClipboardList,
  Edit,
  Trash2
} from 'lucide-react';
import axios from 'axios';

const PrenupManagement = () => {
  const [prenups, setPrenups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrenup, setSelectedPrenup] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bloodTypes, setBloodTypes] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    current: 1,
    total: 1,
    count: 0,
    totalRecords: 0
  });

  useEffect(() => {
    loadPrenups();
    loadReferenceData();
  }, [filters.page]);

  const loadReferenceData = async () => {
    try {
      const [bloodTypesRes, nationalitiesRes] = await Promise.all([
        axios.get('/reference/blood-types'),
        axios.get('/reference/nationalities')
      ]);
      setBloodTypes(bloodTypesRes.data.data || []);
      setNationalities(nationalitiesRes.data.data || []);
    } catch (error) {
      console.error('Error loading reference data:', error);
    }
  };

  const loadPrenups = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get('/admin/prenups', { params: filters });
      setPrenups(response.data.data.prenups || []);
      setPagination(response.data.data.pagination || {
        current: 1,
        total: 1,
        count: 0,
        totalRecords: 0
      });
    } catch (error) {
      console.error('Error loading prenups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const applyFilters = () => {
    loadPrenups();
  };

  const handleViewPrenup = async (prenupId) => {
    try {
      const response = await axios.get(`/admin/prenups/${prenupId}`);
      setSelectedPrenup(response.data.data);
      setShowViewModal(true);
    } catch (error) {
      console.error('Error loading prenup details:', error);
    }
  };

  const handleUpdateStatus = async (prenupId, newStatus) => {
    try {
      await axios.patch(`/admin/prenups/${prenupId}/status`, { status: newStatus });
      
      // Update local state
      setPrenups(prenups.map(prenup => 
        prenup._id === prenupId ? { ...prenup, status: newStatus } : prenup
      ));
      
      // Update selected prenup if modal is open
      if (selectedPrenup && selectedPrenup._id === prenupId) {
        setSelectedPrenup({ ...selectedPrenup, status: newStatus });
      }
      
      setShowViewModal(false);
    } catch (error) {
      console.error('Error updating prenup status:', error);
      alert('خطأ في تحديث حالة الاستمارة');
    }
  };

  const exportPrenups = () => {
    alert('جاري تطوير تصدير البيانات');
  };

  const handleEditPrenup = async (prenupId) => {
    try {
      const response = await axios.get(`/admin/prenups/${prenupId}`);
      const prenup = response.data.data;
      setSelectedPrenup(prenup);
      setEditFormData({
        FIRSTNAME: prenup.FIRSTNAME || '',
        SECONDNAME: prenup.SECONDNAME || '',
        THIRDNAME: prenup.THIRDNAME || '',
        FOURTHNAME: prenup.FOURTHNAME || '',
        PHONE: prenup.PHONE || '',
        ADDRESS: prenup.ADDRESS || '',
        NID: prenup.NID || '',
        PASSPORTNUMBER: prenup.PASSPORTNUMBER || '',
        GENDER: prenup.GENDER || '',
        AGE: prenup.AGE || '',
        HEIGHT: prenup.HEIGHT || '',
        WEIGHT: prenup.WEIGHT || '',
        BLOODTYPEIDFK: prenup.BLOODTYPEIDFK || '',
        RH: prenup.RH || '',
        BLOODPRESSURESYSTOLIC: prenup.BLOODPRESSURESYSTOLIC || '',
        BLOODPRESSUREDIASTOLIC: prenup.BLOODPRESSUREDIASTOLIC || '',
        HB: prenup.HB || '',
        RANDOMGLUCOSEMEASURE: prenup.RANDOMGLUCOSEMEASURE || '',
        HVDBARCODE: prenup.HVDBARCODE || '',
        PARTNERNAME: prenup.PARTNERNAME || '',
        PARTNERNID: prenup.PARTNERNID || '',
        PARTNERPASSPORTNUMBER: prenup.PARTNERPASSPORTNUMBER || ''
      });
      setShowEditModal(true);
    } catch (error) {
      console.error('Error loading prenup for edit:', error);
      alert('خطأ في تحميل بيانات الاستمارة');
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async () => {
    setEditLoading(true);
    try {
      const response = await axios.put(`/admin/prenups/${selectedPrenup._id}`, editFormData);
      if (response.data.success) {
        // Update local state
        setPrenups(prenups.map(p =>
          p._id === selectedPrenup._id ? { ...p, ...editFormData } : p
        ));
        setShowEditModal(false);
        alert('تم تحديث الاستمارة بنجاح');
      }
    } catch (error) {
      console.error('Error updating prenup:', error);
      alert('خطأ في تحديث الاستمارة');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteClick = (prenup) => {
    setSelectedPrenup(prenup);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const response = await axios.delete(`/admin/prenups/${selectedPrenup._id}`);
      if (response.data.success) {
        // Remove from local state
        setPrenups(prenups.filter(p => p._id !== selectedPrenup._id));
        setShowDeleteModal(false);
        alert('تم حذف الاستمارة بنجاح');
      }
    } catch (error) {
      console.error('Error deleting prenup:', error);
      alert('خطأ في حذف الاستمارة');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePrintSheet = (prenupId) => {
    // Open print sheet in new window
    const printUrl = `/print/sheet?SHEETID=${btoa(prenupId)}`;
    window.open(printUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
  };

  const StatusBadge = ({ status }) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'منتظر نتيجة الHVD', icon: Clock },
      approved: { color: 'bg-green-100 text-green-800', text: 'جاهز للطباعة', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', text: 'مرفوضة', icon: XCircle },
      sample_replacement: { color: 'bg-orange-100 text-orange-800', text: 'مطلوب إستبدال العينة', icon: Clock },
      printed: { color: 'bg-blue-100 text-blue-800', text: 'تم الطباعة', icon: CheckCircle }
    };

    const config = statusConfig[status] || statusConfig.approved;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 ml-1" />
        {config.text}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">إدارة استمارات الفحص</h1>
          <p className="mt-1 text-sm text-gray-600">
            مراجعة وإدارة استمارات فحص المقبلين على الزواج
          </p>
        </div>
        <button
          onClick={exportPrenups}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <Download className="w-4 h-4 ml-2" />
          تصدير
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              البحث
            </label>
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="رقم الاستمارة أو الاسم..."
                className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              الحالة
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">جميع الحالات</option>
              <option value="pending">منتظر نتيجة الHVD</option>
              <option value="sample_replacement">مطلوب إستبدال العينة</option>
              <option value="approved">جاهز للطباعة</option>
              <option value="rejected">مرفوضة</option>
              <option value="printed">تم الطباعة</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              من تاريخ
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              إلى تاريخ
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={applyFilters}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Filter className="w-4 h-4 ml-2" />
              تطبيق الفلتر
            </button>
          </div>
        </div>
      </div>

      {/* Prenups Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  رقم الاستمارة
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الاسم الكامل
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الرقم القومي
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  رقم الهاتف
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الحالة
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  تاريخ الإنشاء
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">
                    <div className="animate-pulse">جاري التحميل...</div>
                  </td>
                </tr>
              ) : prenups.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    لا توجد استمارات
                  </td>
                </tr>
              ) : (
                prenups.map((prenup) => (
                  <tr key={prenup._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      {prenup.documentNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {[prenup.FIRSTNAME, prenup.SECONDNAME, prenup.THIRDNAME, prenup.FOURTHNAME].filter(Boolean).join(' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {prenup.NATIONALITY === 'E' ? prenup.NID : prenup.PASSPORTNUMBER}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {prenup.PHONE}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={prenup.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(prenup.createdAt).toLocaleDateString('ar-EG')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2 space-x-reverse">
                        <button
                          onClick={() => handleViewPrenup(prenup._id)}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                        >
                          <Eye className="w-3 h-3 ml-1" />
                          عرض
                        </button>
                        <button
                          onClick={() => handleEditPrenup(prenup._id)}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                        >
                          <Edit className="w-3 h-3 ml-1" />
                          تعديل
                        </button>
                        <button
                          onClick={() => handleDeleteClick(prenup)}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200"
                        >
                          <Trash2 className="w-3 h-3 ml-1" />
                          حذف
                        </button>
                        <button
                          onClick={() => window.open(`/admin/audit-logs?documentNumber=${prenup.documentNumber}`, '_blank')}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                        >
                          <ClipboardList className="w-3 h-3 ml-1" />
                          المعاملات
                        </button>
                        {(prenup.status === 'approved' || prenup.status === 'printed') && (
                          <button
                            onClick={() => handlePrintSheet(prenup._id)}
                            className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200"
                          >
                            <Printer className="w-3 h-3 ml-1" />
                            طباعة
                          </button>
                        )}
                        {prenup.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleUpdateStatus(prenup._id, 'approved')}
                              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200"
                            >
                              <CheckCircle className="w-3 h-3 ml-1" />
                              اعتماد
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(prenup._id, 'sample_replacement')}
                              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700 hover:bg-orange-200"
                            >
                              <Clock className="w-3 h-3 ml-1" />
                              طلب عينة
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(prenup._id, 'rejected')}
                              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200"
                            >
                              <XCircle className="w-3 h-3 ml-1" />
                              رفض
                            </button>
                          </>
                        )}
                        {prenup.status === 'sample_replacement' && (
                          <>
                            <button
                              onClick={() => handleUpdateStatus(prenup._id, 'approved')}
                              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200"
                            >
                              <CheckCircle className="w-3 h-3 ml-1" />
                              اعتماد
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(prenup._id, 'rejected')}
                              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200"
                            >
                              <XCircle className="w-3 h-3 ml-1" />
                              رفض
                            </button>
                          </>
                        )}
                        {prenup.status === 'approved' && (
                          <button
                            onClick={() => handleUpdateStatus(prenup._id, 'printed')}
                            className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                          >
                            <CheckCircle className="w-3 h-3 ml-1" />
                            تم الطباعة
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                السابق
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page === pagination.total}
                className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                التالي
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  عرض {((filters.page - 1) * filters.limit) + 1} إلى{' '}
                  {Math.min(filters.page * filters.limit, pagination.totalRecords)} من{' '}
                  {pagination.totalRecords} نتيجة
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                    disabled={filters.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  {[...Array(pagination.total)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setFilters({ ...filters, page: i + 1 })}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        filters.page === i + 1
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                    disabled={filters.page === pagination.total}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View Prenup Modal */}
      {showViewModal && selectedPrenup && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    تفاصيل الاستمارة - {selectedPrenup.documentNumber}
                  </h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">البيانات الشخصية</h4>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">الاسم الكامل:</span>
                        <p className="font-medium">{[selectedPrenup.FIRSTNAME, selectedPrenup.SECONDNAME, selectedPrenup.THIRDNAME, selectedPrenup.FOURTHNAME].filter(Boolean).join(' ')}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">الرقم القومي:</span>
                        <p className="font-medium">{selectedPrenup.NID || selectedPrenup.PASSPORTNUMBER}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">تاريخ الميلاد:</span>
                        <p className="font-medium">
                          {selectedPrenup.BIRTHDATE ? new Date(selectedPrenup.BIRTHDATE).toLocaleDateString('ar-EG') : 'غير محدد'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">العمر:</span>
                        <p className="font-medium">{selectedPrenup.AGE} سنة</p>
                      </div>
                      <div>
                        <span className="text-gray-500">الجنس:</span>
                        <p className="font-medium">{selectedPrenup.GENDER === 'M' ? 'ذكر' : 'أنثى'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">رقم الهاتف:</span>
                        <p className="font-medium">{selectedPrenup.PHONE}</p>
                      </div>
                    </div>

                    <div>
                      <span className="text-gray-500">عنوان البطاقة:</span>
                      <p className="font-medium">{selectedPrenup.ADDRESS}</p>
                    </div>
                  </div>

                  {/* Medical Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">البيانات الطبية</h4>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">فصيلة الدم:</span>
                        <p className="font-medium">
                          {(() => {
                            const bloodType = bloodTypes.find(bt => bt.id === selectedPrenup.BLOODTYPEIDFK);
                            return bloodType ? `${bloodType.nameAr} (${bloodType.name})` : selectedPrenup.BLOODTYPEIDFK;
                          })()} 
                          ({selectedPrenup.RH === 'Y' ? '+' : '-'})
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">الطول:</span>
                        <p className="font-medium">{selectedPrenup.HEIGHT} سم</p>
                      </div>
                      <div>
                        <span className="text-gray-500">الوزن:</span>
                        <p className="font-medium">{selectedPrenup.WEIGHT} كج</p>
                      </div>
                      <div>
                        <span className="text-gray-500">مؤشر كتلة الجسم:</span>
                        <p className="font-medium">{selectedPrenup.bmi}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">ضغط الدم:</span>
                        <p className="font-medium">
                          {selectedPrenup.BLOODPRESSURESYSTOLIC}/{selectedPrenup.BLOODPRESSUREDIASTOLIC}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">الهيموجلوبين:</span>
                        <p className="font-medium">{selectedPrenup.HB} g/dL</p>
                      </div>
                      <div>
                        <span className="text-gray-500">السكر العشوائي:</span>
                        <p className="font-medium">{selectedPrenup.RANDOMGLUCOSEMEASURE} mg/dL</p>
                      </div>
                      <div>
                        <span className="text-gray-500">باركود العينة:</span>
                        <p className="font-medium font-mono">{selectedPrenup.HVDBARCODE}</p>
                      </div>
                    </div>

                    {/* Virus Tests */}
                    <div className="mt-4 pt-4 border-t">
                      <h5 className="font-medium text-gray-700 mb-2">الفحوصات الفيروسية</h5>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Anti-HCV</div>
                          <div className={`font-medium ${selectedPrenup.ANTIHCV === 'Y' || selectedPrenup.ANTIHCV === '1' ? 'text-red-600' : 'text-green-600'}`}>
                            {selectedPrenup.ANTIHCV === 'Y' || selectedPrenup.ANTIHCV === '1' ? 'إيجابي' : 'سلبي'}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">HBsAg</div>
                          <div className={`font-medium ${selectedPrenup.HBSAG === 'Y' || selectedPrenup.HBSAG === '1' ? 'text-red-600' : 'text-green-600'}`}>
                            {selectedPrenup.HBSAG === 'Y' || selectedPrenup.HBSAG === '1' ? 'إيجابي' : 'سلبي'}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Anti-HIV</div>
                          <div className={`font-medium ${selectedPrenup.ANTIHIV === 'Y' || selectedPrenup.ANTIHIV === '1' ? 'text-red-600' : 'text-green-600'}`}>
                            {selectedPrenup.ANTIHIV === 'Y' || selectedPrenup.ANTIHIV === '1' ? 'إيجابي' : 'سلبي'}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Medical History */}
                    <div className="mt-4 pt-4 border-t">
                      <h5 className="font-medium text-gray-700 mb-2">التاريخ الطبي</h5>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">ضغط الدم:</span>
                          <p className="font-medium">{selectedPrenup.BLDPRESSURE === 'Y' ? 'يعاني من ضغط الدم' : 'طبيعي'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">السكري:</span>
                          <p className="font-medium">{selectedPrenup.DIABETIC === 'Y' ? 'يعاني من السكري' : 'طبيعي'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">نقل دم سابق:</span>
                          <p className="font-medium">{selectedPrenup.BLOODTRANSFER === 'Y' || selectedPrenup.BLOODTRANSFER === '1' ? 'نعم' : 'لا'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Partner Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">بيانات الشريك</h4>
                    
                    <div className="text-sm space-y-2">
                      <div>
                        <span className="text-gray-500">اسم الشريك:</span>
                        <p className="font-medium">{selectedPrenup.PARTNERNAME}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">جنسية الشريك:</span>
                        <p className="font-medium">{selectedPrenup.PARTNERNATIONALITY === 'E' ? 'مصري' : 'غير مصري'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">{selectedPrenup.PARTNERNATIONALITY === 'E' ? 'الرقم القومي للشريك' : 'رقم جواز الشريك'}:</span>
                        <p className="font-medium">{selectedPrenup.PARTNERNATIONALITY === 'E' ? selectedPrenup.PARTNERNID : selectedPrenup.PARTNERPASSPORTNUMBER}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">قرابة بينهما:</span>
                        <p className="font-medium">{selectedPrenup.ISRELATEDQUES === 'Y' ? 'نعم' : 'لا'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">زواج سابق:</span>
                        <p className="font-medium">{selectedPrenup.PREVMARRIAGEQUES === 'Y' ? 'نعم' : 'لا'}</p>
                      </div>
                      {selectedPrenup.EXISTCHILDRENQUES && (
                        <div>
                          <span className="text-gray-500">وجود أطفال:</span>
                          <p className="font-medium">{selectedPrenup.EXISTCHILDRENQUES === 'Y' ? 'نعم' : 'لا'}</p>
                        </div>
                      )}
                      {selectedPrenup.GENEDISEASEQUES && (
                        <div>
                          <span className="text-gray-500">أمراض وراثية:</span>
                          <p className="font-medium">{selectedPrenup.GENEDISEASEQUES === 'Y' ? 'نعم' : 'لا'}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status and Actions */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">حالة الاستمارة</h4>
                    
                    <div className="flex items-center space-x-4 space-x-reverse">
                      <StatusBadge status={selectedPrenup.status} />
                      <span className="text-sm text-gray-500">
                        تم الإنشاء: {new Date(selectedPrenup.createdAt).toLocaleDateString('ar-EG')}
                      </span>
                    </div>

                    {(selectedPrenup.status === 'pending' || selectedPrenup.status === 'sample_replacement') && (
                      <div className="flex space-x-3 space-x-reverse pt-4">
                        <button
                          onClick={() => handleUpdateStatus(selectedPrenup._id, 'approved')}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 ml-2" />
                          اعتماد الاستمارة
                        </button>
                        {selectedPrenup.status === 'pending' && (
                          <button
                            onClick={() => handleUpdateStatus(selectedPrenup._id, 'sample_replacement')}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
                          >
                            <Clock className="w-4 h-4 ml-2" />
                            طلب استبدال العينة
                          </button>
                        )}
                        <button
                          onClick={() => handleUpdateStatus(selectedPrenup._id, 'rejected')}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                        >
                          <XCircle className="w-4 h-4 ml-2" />
                          رفض الاستمارة
                        </button>
                      </div>
                    )}
                    
                    {selectedPrenup.status === 'approved' && (
                      <div className="flex space-x-3 space-x-reverse pt-4">
                        <button
                          onClick={() => handlePrintSheet(selectedPrenup._id)}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
                        >
                          <Printer className="w-4 h-4 ml-2" />
                          طباعة الاستمارة
                        </button>
                        <button
                          onClick={() => handleUpdateStatus(selectedPrenup._id, 'printed')}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                        >
                          <CheckCircle className="w-4 h-4 ml-2" />
                          تأكيد الطباعة
                        </button>
                      </div>
                    )}
                    
                    {selectedPrenup.status === 'printed' && (
                      <div className="flex space-x-3 space-x-reverse pt-4">
                        <button
                          onClick={() => handlePrintSheet(selectedPrenup._id)}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
                        >
                          <Printer className="w-4 h-4 ml-2" />
                          إعادة طباعة
                        </button>
                      </div>
                    )}
                    
                    {selectedPrenup.statusHistory && selectedPrenup.statusHistory.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <h5 className="text-sm font-medium text-gray-900 mb-2">سجل الحالة</h5>
                        <div className="space-y-2">
                          {selectedPrenup.statusHistory.slice(-3).map((history, index) => (
                            <div key={index} className="text-sm text-gray-600">
                              <span className="font-medium">{history.status}</span> - 
                              <span className="text-xs"> {new Date(history.changedAt).toLocaleString('ar-EG')}</span>
                              {history.reason && <span className="text-xs italic"> - {history.reason}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Prenup Modal */}
      {showEditModal && selectedPrenup && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowEditModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    تعديل الاستمارة - {selectedPrenup.documentNumber}
                  </h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-h-96 overflow-y-auto">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">البيانات الشخصية</h4>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">الاسم الأول</label>
                        <input
                          type="text"
                          name="FIRSTNAME"
                          value={editFormData.FIRSTNAME}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-right"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">الاسم الثاني</label>
                        <input
                          type="text"
                          name="SECONDNAME"
                          value={editFormData.SECONDNAME}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-right"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">الاسم الثالث</label>
                        <input
                          type="text"
                          name="THIRDNAME"
                          value={editFormData.THIRDNAME}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-right"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">الاسم الرابع</label>
                        <input
                          type="text"
                          name="FOURTHNAME"
                          value={editFormData.FOURTHNAME}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-right"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهاتف</label>
                      <input
                        type="text"
                        name="PHONE"
                        value={editFormData.PHONE}
                        onChange={handleEditFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-right"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">العنوان</label>
                      <input
                        type="text"
                        name="ADDRESS"
                        value={editFormData.ADDRESS}
                        onChange={handleEditFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-right"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">الرقم القومي</label>
                        <input
                          type="text"
                          name="NID"
                          value={editFormData.NID}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-left"
                          dir="ltr"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">العمر</label>
                        <input
                          type="number"
                          name="AGE"
                          value={editFormData.AGE}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-left"
                          dir="ltr"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Medical Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">البيانات الطبية</h4>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">الطول (سم)</label>
                        <input
                          type="number"
                          name="HEIGHT"
                          value={editFormData.HEIGHT}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-left"
                          dir="ltr"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">الوزن (كجم)</label>
                        <input
                          type="number"
                          name="WEIGHT"
                          value={editFormData.WEIGHT}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-left"
                          dir="ltr"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ضغط الدم الانقباضي</label>
                        <input
                          type="number"
                          name="BLOODPRESSURESYSTOLIC"
                          value={editFormData.BLOODPRESSURESYSTOLIC}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-left"
                          dir="ltr"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ضغط الدم الانبساطي</label>
                        <input
                          type="number"
                          name="BLOODPRESSUREDIASTOLIC"
                          value={editFormData.BLOODPRESSUREDIASTOLIC}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-left"
                          dir="ltr"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">الهيموجلوبين</label>
                        <input
                          type="number"
                          step="0.1"
                          name="HB"
                          value={editFormData.HB}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-left"
                          dir="ltr"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">السكر العشوائي</label>
                        <input
                          type="number"
                          name="RANDOMGLUCOSEMEASURE"
                          value={editFormData.RANDOMGLUCOSEMEASURE}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-left"
                          dir="ltr"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">باركود العينة</label>
                      <input
                        type="text"
                        name="HVDBARCODE"
                        value={editFormData.HVDBARCODE}
                        onChange={handleEditFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-left font-mono"
                        dir="ltr"
                      />
                    </div>

                    <h4 className="font-medium text-gray-900 border-b pb-2 mt-4">بيانات الشريك</h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">اسم الشريك</label>
                      <input
                        type="text"
                        name="PARTNERNAME"
                        value={editFormData.PARTNERNAME}
                        onChange={handleEditFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-right"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">الرقم القومي للشريك</label>
                        <input
                          type="text"
                          name="PARTNERNID"
                          value={editFormData.PARTNERNID}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-left"
                          dir="ltr"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">جواز سفر الشريك</label>
                        <input
                          type="text"
                          name="PARTNERPASSPORTNUMBER"
                          value={editFormData.PARTNERPASSPORTNUMBER}
                          onChange={handleEditFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-left"
                          dir="ltr"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3 space-x-reverse">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={editLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {editLoading ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedPrenup && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:mr-4 sm:text-right">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      حذف الاستمارة
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        هل أنت متأكد من حذف الاستمارة رقم <span className="font-bold text-gray-900">{selectedPrenup.documentNumber}</span>؟
                        <br />
                        الاسم: {[selectedPrenup.FIRSTNAME, selectedPrenup.SECONDNAME, selectedPrenup.THIRDNAME, selectedPrenup.FOURTHNAME].filter(Boolean).join(' ')}
                        <br />
                        <span className="text-red-600">هذا الإجراء لا يمكن التراجع عنه.</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={deleteLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mr-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {deleteLoading ? 'جاري الحذف...' : 'نعم، احذف'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrenupManagement;